import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from './ui/Button';
import TextInput from './ui/TextInput';
import { Label } from './ui/Label';
import { cn } from '../lib/utils';
import { APP_BRAND } from '../config/branding';
import AppBrandIcon from './branding/AppBrandIcon';
import { authService } from '../services/authService';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const { t } = useTranslation('login');
  const productName = appConfig.system.appName?.trim() || APP_BRAND.name;

  useDocumentTitle(t('forgotTitle'));

  const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    if (!email) {
      setEmailError(t('emailRequired'));
      return;
    }
    if (!validateEmail(email)) {
      setEmailError(t('emailInvalid'));
      return;
    }

    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
      setSuccess(true);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || t('forgotError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('min-h-screen flex items-center justify-center px-4 py-10', colors.background)}>
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <AppBrandIcon className="h-11 w-11" iconClassName="h-5 w-5" />
          <div>
            <p className={cn('text-lg font-bold', colors.text)}>{productName}</p>
            <p className={cn('text-sm', colors.textMuted)}>{t('forgotHeading')}</p>
          </div>
        </div>

        <div className={cn('rounded-2xl border p-8 shadow-sm', colors.surface, colors.border)}>
          <h2 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>{t('forgotTitle')}</h2>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>
            {t('forgotSubtitle')}
          </p>

          {success ? (
            <div className="mt-6 space-y-4">
              <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.success)} role="status">
                {t('forgotSuccess')}
              </div>
              <Link to="/login" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                {t('backToSignIn')}
              </Link>
            </div>
          ) : (
            <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.error)} role="alert">
                  {error}
                </div>
              )}
              <div>
                <Label htmlFor="email">{t('email')}</Label>
                <TextInput
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('emailPlaceholder')}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  className="mt-1.5"
                  aria-invalid={!!emailError}
                />
                {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
              </div>
              <Button type="submit" className="w-full" loading={isLoading} disabled={isLoading}>
                {t('sendResetLink')}
              </Button>
              <Link to="/login" className="block text-center text-sm text-indigo-600 hover:text-indigo-700">
                {t('backToSignIn')}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
